import { useState } from "react";
import { X } from "lucide-react";
import { useCreateTask } from "@/features/tasks/hooks/useTasks";
import { useProjects } from "@/features/tasks/hooks/useProjects";
import { WeekSelect } from "@/components/ui/WeekSelect";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { naturalSortKey } from "@/utils/naturalSort";

interface Props {
  open: boolean;
  onClose: () => void;
  projects?: { id: string; short_name: string; name: string }[];
}

export function CreateTaskModal({ open, onClose, projects: projectsProp }: Props) {
  const { mutate: createTask, isPending } = useCreateTask();
  const { data: fetchedProjects = [] } = useProjects();
  const projects = projectsProp ?? fetchedProjects;
  const [title, setTitle] = useState("");
  const [project, setProject] = useState("");
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [error, setError] = useState("");

  function handleSubmit() {
    if (!title.trim()) { setError("Tytuł jest wymagany."); return; }
    setError("");
    createTask(
      { title: title.trim(), project: project || null, due_date: dueDate },
      {
        onSuccess: () => {
          setTitle("");
          setProject("");
          setDueDate(null);
          onClose();
        },
      }
    );
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Nowe zadanie</h2>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-6 py-5">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tytuł <span className="text-red-500">*</span>
            </label>
            <input
              autoFocus
              value={title}
              onChange={(e) => { setTitle(e.target.value); setError(""); }}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              placeholder="Np. Dokumentacja projektu..."
            />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>

          {/* Project */}
          <SearchableSelect
            label="Przedsięwzięcie"
            placeholder="— brak —"
            value={project}
            onChange={(e) => setProject(e.target.value)}
            options={projects
              .slice()
              .sort((a, b) => naturalSortKey(a.short_name).localeCompare(naturalSortKey(b.short_name), "pl"))
              .map((p) => ({ value: p.id, label: `${p.short_name} – ${p.name}` }))}
          />

          {/* Due week */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Termin końcowy</label>
            <WeekSelect
              value={dueDate}
              onChange={setDueDate}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900">
            Anuluj
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending}
            className="px-4 py-2 text-sm bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
          >
            {isPending ? "Tworzenie..." : "Utwórz zadanie"}
          </button>
        </div>
      </div>
    </div>
  );
}
